import { EmployeeRole } from "@1games-ru/region-shared/dist/enums/business/employee";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import {
  removeEmployee,
  setEmployeesIsDismissModal,
  updateEmployee,
} from "@/domains/business/slice";
import toast from "@/domains/toasts/helpers/toast";

export function useEmployeesHandlers() {
  const { t, i18n } = useTranslation("business");
  const dispatch = useAppDispatch();

  const [isPending, setIsPending] = useState(false);
  const [isSelfRemovingLoading, setIsSelfRemovingLoading] = useState(false);

  const addEmployeeHandler = useCallback(
    async (businessId: number, characterId: number) => {
      try {
        const response = await BusinessMpProvider.addEmployee(businessId, characterId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          toast.success(t("business.toast.employeeAdded"));
        } else if (
          response?.error?.message &&
          (i18n.exists(`business:business.errors.${response.error.message}`) ||
            i18n.exists(`business.errors.${response.error.message}`))
        ) {
          console.error(response.error.message);
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response?.error?.message ?? "Unknown RPC error");
          toast.error(t("business.toast.employeeNotAdded"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.employeeNotAdded"));
      }
    },
    [i18n, t],
  );

  const updateEmployeeHandler = useCallback(
    async (businessId: number, characterId: number, role: EmployeeRole) => {
      try {
        const response = await BusinessMpProvider.updateEmployee(businessId, characterId, role);

        if (response instanceof RpcMpProviderSuccessResponse) {
          dispatch(updateEmployee({ id: characterId, newRole: role }));
          toast.success(t("business.toast.employeeUpdated"));
        } else if (
          response?.error?.message &&
          (i18n.exists(`business:business.errors.${response.error.message}`) ||
            i18n.exists(`business.errors.${response.error.message}`))
        ) {
          console.error(response.error.message);
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response?.error?.message ?? "Unknown RPC error");
          toast.error(t("business.toast.employeeNotUpdated"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.employeeNotUpdated"));
      }
    },
    [dispatch, i18n, t],
  );

  const removeEmployeeHandler = useCallback(
    async (businessId: number, characterId: number) => {
      setIsPending(true);
      try {
        const response = await BusinessMpProvider.removeEmployee(businessId, characterId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          dispatch(removeEmployee(characterId));
          dispatch(setEmployeesIsDismissModal(false));
          toast.success(t("business.toast.employeeRemoved"));
        } else if (
          response?.error?.message &&
          (i18n.exists(`business:business.errors.${response.error.message}`) ||
            i18n.exists(`business.errors.${response.error.message}`))
        ) {
          console.error(response.error.message);
          dispatch(setEmployeesIsDismissModal(false));
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response?.error?.message ?? "Unknown RPC error");
          dispatch(setEmployeesIsDismissModal(false));
          toast.error(t("business.toast.employeeNotRemoved"));
        }
      } catch (e) {
        console.error(e);
        dispatch(setEmployeesIsDismissModal(false));
        toast.error(t("business.toast.employeeNotRemoved"));
      } finally {
        setIsPending(false);
      }
    },
    [dispatch, i18n, t],
  );

  const removeEmployeeSelfHandler = useCallback(
    async (businessId: number, cb?: () => void) => {
      setIsSelfRemovingLoading(true);
      try {
        const response = await BusinessMpProvider.removeSelfFromBusiness(businessId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          if (cb) cb();
          toast.success(t("business.toast.employeeRemoveSelf"));
        } else if (
          response?.error?.message &&
          (i18n.exists(`business:business.errors.${response.error.message}`) ||
            i18n.exists(`business.errors.${response.error.message}`))
        ) {
          console.error(response.error.message);
          dispatch(setEmployeesIsDismissModal(false));
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response?.error?.message ?? "Unknown RPC error");
          dispatch(setEmployeesIsDismissModal(false));
          toast.error(t("business.toast.employeeRemoveSelfError"));
        }
      } catch (e) {
        console.error(e);
        dispatch(setEmployeesIsDismissModal(false));
        toast.error(t("business.toast.employeeRemoveSelfError"));
      } finally {
        setIsSelfRemovingLoading(false);
      }
    },
    [dispatch, i18n, t],
  );

  return {
    isPending,
    addEmployeeHandler,
    updateEmployeeHandler,
    removeEmployeeHandler,
    isSelfRemovingLoading,
    removeEmployeeSelfHandler,
  };
}
